<template>
    <grid :columnDefs="columnDefs" :rowData="rowData" :isAutoWidth="true" :isAutoHeight="true"> </grid>
</template>

<script>
import api from '../../../api';
import { saveAs } from 'file-saver';

import { GUEST_VERIFICATIONS_ENDPOINT } from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import grid from '../../generic/grid/grid.vue';
import PhotoActionsCellRenderer from './photo-actions-cell-renderer.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import { mapGetters } from 'vuex';
import StatusCellRenderer from './status-cell-renderer.vue';

export default {
    components: {
        grid,
        photoActionsCellRenderer: PhotoActionsCellRenderer,
        actionsCellRenderer: ActionsCellRenderer,
        statusCellRenderer: StatusCellRenderer,
    },
    data() {
        return {
            rowData: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        columnDefs() {
            return [
                {
                    field: 'meta_reservation',
                    headerName: 'Invoice',
                    cellRenderer: ({ data }) =>
                        `<a href="/app/reservation/edit/invoice/${data.meta_reservation}">${data.meta_reservation}</a>`,
                },
                {
                    field: 'created',
                    type: 'dateTimeColumn',
                    headerName: 'Created',
                },
                {
                    field: 'arrival_date',
                    headerName: 'Arrival Date',
                    type: 'dateColumn',
                },
                {
                    field: 'guest',
                    headerName: 'Guest',
                },
                {
                    field: 'seen',
                    headerName: 'Seen',
                    type: 'yesNoColumn',
                },
                {
                    field: 'seen_on',
                    headerName: 'Seen On',
                    type: 'dateColumn',
                    minWidth: 150,
                    cellRendererParams: {
                        noValue: 'Not seen',
                    },
                },
                {
                    field: 'verified_on',
                    headerName: 'Status',
                    minWidth: 350,
                    autoWidthExclude: true,
                    cellRenderer: 'statusCellRenderer',
                    cellRendererParams: {
                        verify: async (id, modalId) => {
                            try {
                                this.$parent.$emit('handleBusy', true);
                                await api.post(`${GUEST_VERIFICATIONS_ENDPOINT(id)}verify/`);
                                this.$bvModal.hide(modalId);
                                this.fetchData();
                                this.successToast({ text: 'Document was sucessfully verified' });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not verify document',
                                });
                            } finally {
                                this.$parent.$emit('handleBusy', false);
                            }
                        },
                    },
                },
                {
                    field: '',
                    minWidth: 100,
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        verify: async (id, modalId) => {
                            try {
                                this.$parent.$emit('handleBusy', true);
                                await api.post(`${GUEST_VERIFICATIONS_ENDPOINT(id)}verify/`);
                                this.$bvModal.hide(modalId);
                                this.fetchData();
                                this.successToast({ text: 'Document was sucessfully verified' });
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not verify document',
                                });
                            } finally {
                                this.$parent.$emit('handleBusy', false);
                            }
                        },
                    },
                },
                {
                    field: 'image',
                    headerName: 'Photo',
                    cellRenderer: 'photoActionsCellRenderer',
                    minWidth: 180,
                    cellRendererParams: {
                        requestImage: async (id) => {
                            try {
                                this.$parent.$emit('handleBusy', true);
                                const response = await api.get(
                                    `${GUEST_VERIFICATIONS_ENDPOINT(id)}request-verification-image/`,
                                    { responseType: 'blob' }
                                );
                                saveAs(response.data, this.getFilenameFromResponse(response));
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not download image',
                                });
                            } finally {
                                this.$parent.$emit('handleBusy', false);
                            }
                        },
                        requestSelfieImage: async (id) => {
                            try {
                                this.$parent.$emit('handleBusy', true);
                                const response = await api.get(
                                    `${GUEST_VERIFICATIONS_ENDPOINT(id)}request-verification-selfie-image/`,
                                    { responseType: 'blob' }
                                );
                                saveAs(response.data, this.getFilenameFromResponse(response));
                            } catch (e) {
                                try {
                                    const error = await e.response?.data?.text();
                                    const message = JSON.parse(error);
                                    if (message.detail) {
                                        this.errorToast({
                                            text: "Guest didn't upload selfie image",
                                        });
                                    }
                                } catch {
                                    const { generic } = formatResponseError(e);
                                    this.errorToast({
                                        text: generic || 'Could not download image',
                                    });
                                }
                            } finally {
                                this.$parent.$emit('handleBusy', false);
                            }
                        },
                    },
                },
            ];
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            try {
                this.$parent.$emit('handleBusy', true);
                const { data } = await api.get(`${GUEST_VERIFICATIONS_ENDPOINT()}requested/`);
                this.rowData = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not fetch verifications',
                });
            } finally {
                this.$parent.$emit('handleBusy', false);
            }
        },
        getFilenameFromResponse(response) {
            return response.headers['content-disposition'].split(';')[1].split('=')[1].split('"')[1].trim();
        },
    },
};
</script>

<style>
</style>